import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Heart, CheckCircle, Lightbulb } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function HomePage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[56vh] flex items-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-teal-500/30">
              <Lightbulb className="w-4 h-4" />
              <span>Productive Offenders of Society Foundation</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Reentry & Recovery Resources
            </h1>
            
            <div className="flex flex-wrap gap-4 mt-8">
              <Button asChild size="lg" className="bg-teal-500 hover:bg-teal-400 text-white shadow-lg transition-all duration-300">
                <Link to="/reentry-support">
                  <Phone className="w-4 h-4 mr-2" />
                  Get Help Now
                </Link>
              </Button>
              <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-400 text-white shadow-lg transition-all duration-300">
                <Link to="/get-involved#donate">
                  <Heart className="w-4 h-4 mr-2" />
                  Support the Mission
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">Who This Is For</h2>
          
          <p className="text-lg text-slate-600 mb-6">
            This website is designed to support justice-involved individuals, including those who are currently incarcerated, formerly incarcerated, or preparing for release, as well as their families and advocates.
          </p>
          
          <p className="text-lg text-slate-600">
            The Productive Offenders of Society Foundation (POOSF) exists to provide starting points that help individuals navigate reentry into society with dignity, information, and access to support.
          </p>
        </div>
      </section>

      {/* Our Role */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">Our Role</h2>
          
          <p className="text-lg text-slate-600 mb-6">
            POOSF does not provide direct treatment, housing, or clinical services nationwide.
          </p>
          
          <p className="text-lg text-slate-600 mb-6">
            Instead, POOSF offers resource navigation by connecting individuals to public and legitimate resources offered by:
          </p>
          
          <ul className="space-y-3 text-lg text-slate-600 mb-8">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Nonprofit organizations
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Government agencies (local, state, and federal)
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Community-based programs
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              For-profit organizations that provide publicly available services
            </li>
          </ul>
          
          <p className="text-lg text-slate-600 italic">
            All information on this site is provided for general informational and navigation purposes only.
          </p>
        </div>
      </section>

      {/* Why This Resource Directory Exists */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">Why This Resource Directory Exists</h2>
          
          <p className="text-lg text-slate-600 mb-6">POOSF built this directory to:</p>
          
          <ul className="space-y-3 text-lg text-slate-600">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Remove barriers
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Increase access
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Support equity
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Reduce confusion
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Help people in crisis find help faster
            </li>
          </ul>
        </div>
      </section>

      {/* This Navigation Model Is Common Nationwide */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-8">This Navigation Model Is Common Nationwide</h2>
          
          <p className="text-lg text-slate-300 mb-6">Many established systems use the same approach:</p>
          
          <ul className="space-y-3 text-lg text-slate-300 mb-8">
            <li className="flex items-start gap-3">
              <span className="text-teal-400">→</span>
              United Way 211 → all states
            </li>
            <li className="flex items-start gap-3">
              <span className="text-teal-400">→</span>
              SAMHSA Treatment Locator → national
            </li>
            <li className="flex items-start gap-3">
              <span className="text-teal-400">→</span>
              Legal Aid directories → national and state-based
            </li>
            <li className="flex items-start gap-3">
              <span className="text-teal-400">→</span>
              Reentry councils → state by state
            </li>
          </ul>
          
          <p className="text-lg text-teal-400 font-medium">
            POOSF follows this same public-service navigation model.
          </p>
        </div>
      </section>

      {/* Reentry & Recovery: Addressing Both */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">Reentry & Recovery: Addressing Both</h2>
          
          <p className="text-lg text-slate-600 mb-6">
            Reentry and recovery are often interconnected. Many individuals returning to the community face both reentry barriers and substance use challenges.
          </p>
          
          <p className="text-lg text-slate-600 mb-6">
            POOSF intentionally provides resource navigation for both reentry and recovery, because addressing one without the other often leads to instability.
          </p>
          
          <p className="text-lg text-slate-600 mb-4">Our goal is to:</p>
          
          <ul className="space-y-3 text-lg text-slate-600">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Reduce stigma
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Support informed decision-making
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Help individuals find pathways to stability, recovery, and reintegration
            </li>
          </ul>
        </div>
      </section>

      {/* Residential Reentry Programs */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">Residential Reentry Programs</h2>
          
          <p className="text-lg text-slate-600 mb-6">
            There are several types of residential reentry programs, which vary by state and availability. These may include:
          </p>
          
          <ul className="space-y-3 text-lg text-slate-600 mb-8">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Transitional living programs
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Halfway houses
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Reentry-focused recovery residences
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Community-based residential support programs
            </li>
          </ul>
          
          <p className="text-lg text-slate-600 italic">
            Eligibility, length of stay, and requirements vary by program and jurisdiction.
          </p>
        </div>
      </section>

      {/* Recovery & Substance Use Support */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">Recovery & Substance Use Support</h2>
          
          <p className="text-lg text-slate-600 mb-6">
            Because substance use and reentry challenges often overlap, POOSF includes recovery-focused resource navigation, which may include:
          </p>
          
          <ul className="space-y-3 text-lg text-slate-600 mb-8">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Treatment providers
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Recovery support services
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              Peer recovery programs
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              National and state-level substance use helplines
            </li>
          </ul>
          
          <p className="text-lg text-slate-600 mb-4">
            Examples may include 24-hour hotlines, referral services, and treatment directories.
          </p>
          
          <p className="text-lg text-slate-600 italic">
            All listed resources are public and legitimate, but operate independently.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Find Resources?
          </h2>
          <p className="text-xl text-teal-100 mb-10">
            Explore our comprehensive resource directory or learn about reentry support.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-teal-700 hover:bg-teal-50">
              <Link to="/reentry-support">
                Reentry Support
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-400 text-white">
              <Link to="/resources">
                Resource Directory
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
